import React, { useState, useMemo, useRef, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    TextField,
    Box,
    IconButton,
} from "@mui/material";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { Search } from "@mui/icons-material";
import AnalystName from "../common/AnalystName";
import { Link } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import Checkbox from "@mui/material/Checkbox";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    useDeleteClientMutation,
    useDeleteUserMutation,
} from "../../redux/service/modules/admin";
import { useSelector } from "react-redux";
import { CommonDialog } from "./CommonDialog";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import classNames from "classnames";
import { useNotification } from "../../context/NotificationContext";

const StyledTableCell = styled(TableCell)(
    ({
        theme,
        columnIndex,
        stickyColumnsCount,
        rowIndex,
        maxDepth,
        setHeaderHeight,
    }) => ({
        "&.MuiTableCell-head": {
            // padding: "8px",

            border: "none",
            whiteSpace: "nowrap",
            ...(setHeaderHeight && { height: setHeaderHeight }),
        },
        ...(columnIndex < stickyColumnsCount && {
            position: "sticky",
            zIndex: maxDepth + columnIndex + 1,
        }),
    })
);

const StyledTableRow = styled(TableRow)({
    "&.MuiTableRow-root": {
        height: "auto",
        minHeight: "2rem",
    },
});

const StyledTotalRow = styled(TableRow)({
    position: "sticky",
    bottom: 0,
    backgroundColor: "#faf9f8",
    zIndex: 2,
    "& td": {
        fontWeight: "bold",
        "&:first-of-type": {
            color: "#ED1C24",
        },
    },
});
const getNestedValue = (obj, path) => {
    if (path && path.startsWith("dummy-")) {
        return "";
    }

    try {
        const parts = path?.split(".");
        let value = obj;
        for (let i = 0; i < parts?.length; i++) {
            if (value && value[parts[i]] !== undefined) {
                value = value[parts[i]];
            } else {
                const remainingPath = parts.slice(i).join("_");
                return value[remainingPath] ?? "";
            }
        }
        if (path === "tier_color") {
            return value + "25" ?? "";
        }
        return value ?? "";
    } catch (e) {
        console.error("Error accessing nested value:", e);
        return "";
    }
};

// Convert percentage strings to numbers for proper sorting
const parseValueForSorting = (value) => {
    // Handle null/undefined values
    if (value === null || value === undefined || value === "") {
        return 0;
    }

    // If it's already a number, return it
    if (typeof value === 'number') {
        return value;
    }

    // If it's a string that contains a percentage
    if (typeof value === 'string' && value.includes('%')) {
        // Remove the percentage sign and convert to number
        return parseFloat(value.replace('%', ''));
    }

    // For other string values, try to parse as number if possible
    if (typeof value === 'string') {
        const parsed = parseFloat(value);
        if (!isNaN(parsed)) {
            return parsed;
        }
    }

    // Return original value for non-numeric strings
    return value;
};

const flattenHeaders = (headers, parentKey = "", headerGap = false) => {
    let flattened = [];
    headers.forEach((header, index) => {
        const currentKey = parentKey ? `${header.key}` : header.key;

        // Add dummy header only if headerGap is true AND the header has subheaders
        if (
            headerGap &&
            index > 0 &&
            !parentKey &&
            header.subheaders?.length > 0
        ) {
            flattened.push({
                key: `dummy-${header.key}`,
                name: "",
                value: "",
                isDummy: true,
            });
        }

        if (header.subheaders && header.subheaders.length > 0) {
            flattened = [
                ...flattened,
                { ...header, key: currentKey, isGroup: true },
                ...flattenHeaders(header.subheaders, currentKey, headerGap),
            ];
        } else {
            flattened.push({ ...header, key: currentKey });
        }
    });
    return flattened;
};

const getHeaderDepth = (headers) => {
    let maxDepth = 1;
    headers.forEach((header) => {
        if (header.subheaders && header.subheaders.length > 0) {
            const subDepth = getHeaderDepth(header.subheaders) + 1;
            maxDepth = Math.max(maxDepth, subDepth);
        }
    });
    return maxDepth;
};

const getLeafCount = (header) => {
    if (!header.subheaders || header.subheaders.length === 0) return 1;
    return header.subheaders.reduce((sum, sh) => sum + getLeafCount(sh), 0);
};

const organizeHeadersByRow = (headers, maxDepth, headerGap = false) => {
    const rows = Array(maxDepth)
        .fill()
        .map(() => []);

    const processHeader = (header, currentDepth = 0, colSpan = 1) => {
        if (header.subheaders && header.subheaders.length > 0) {
            // Add dummy column only if headerGap is true AND the next header has subheaders
            if (
                headerGap &&
                currentDepth === 0 &&
                rows[currentDepth].length > 0 &&
                header.subheaders?.length > 0
            ) {
                rows[currentDepth].push({
                    key: `dummy-${header.key}`,
                    name: "",
                    value: "",
                    isDummy: true,
                    colSpan: 1,
                    rowSpan: maxDepth,
                });
            }

            rows[currentDepth].push({
                ...header,
                colSpan: header.subheaders.reduce(
                    (sum, sh) => sum + getLeafCount(sh),
                    0
                ),
                rowSpan: 1,
            });

            header.subheaders.forEach((sh) =>
                processHeader(sh, currentDepth + 1)
            );
        } else {
            rows[currentDepth].push({
                ...header,
                colSpan: 1,
                rowSpan: maxDepth - currentDepth,
            });
        }
    };

    headers.forEach((header) => processHeader(header));
    return rows;
};

const StyledTableContainer = styled(TableContainer)`
    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
        background-color: #f5f5f5;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 3px;
        background-color: #bdbdbd;
    }

    &::-webkit-scrollbar-track {
        border-radius: 3px;
        background-color: #f5f5f5;
    }

    .MuiTable-root {
        min-width: auto;
    }
`;

const highlightKeys = [
    "key_reports_summary",
    "all_reports_summary",
    "bespoke_total",
    "weighted_score",
    "weighted_d_score",
    "bespoke",
    "interactions",
    "client_votes",
    "overall_avg_votes",
    "overall_avg_weighted_score",
    "total_interactions",
    "total_key_interactions",
];

const StyledTableSortLabel = styled(TableSortLabel)({
    "& .MuiTableSortLabel-icon": {
        color: "#ED1C24 !important",
        opacity: 0,
        transition: "opacity 0.2s ease",
        display: "none",
    },
    "&.Mui-active": {
        "& .MuiTableSortLabel-icon": {
            opacity: 1,
            display: "inline-flex",
        },
    },
    "&:hover .MuiTableSortLabel-icon": {
        opacity: 1,
        display: "inline-flex",
    },
});

const StyledCheckbox = styled(Checkbox)({
    "&.MuiCheckbox-root": {
        color: "#ED1C24",
    },
    "&.Mui-checked": {
        color: "#ED1C24",
    },
    "&.MuiCheckbox-indeterminate": {
        color: "#ED1C24",
    },
    "& .MuiSvgIcon-root": {
        fontSize: "20px",
    },
    "&:not(.Mui-checked):not(.MuiCheckbox-indeterminate) .MuiSvgIcon-root": {
        borderRadius: "2px",
        backgroundColor: "white",
    },
});

const CommonTable = ({
    title,
    headers,
    data,
    maxRows,
    onSort,
    onSearch,
    hasPagination,
    totalText,
    firstColumnWidth,
    isHeaderBold,
    isColumnSticky,
    stickyColumnsCount,
    headerFontSizes,
    alignItems,
    highlightRules = [],
    showEditColumn = false,
    showCheckboxColumn = false,
    onSelectRows,
    refetch,
    customCellColor,
    customCellRender,
    dialogType,
    visibleColumns = [],
    setHeaderHeight,
    isClient,
    headerHeights,
    headerPadding,
    headerGap = false,
}) => {
    const [orderBy, setOrderBy] = useState("");
    const [order, setOrder] = useState("asc");
    const [searchFields, setSearchFields] = useState({});
    const [activeHeader, setActiveHeader] = useState(null);
    const [columnWidths, setColumnWidths] = useState([]);
    const columnRefs = useRef([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [deleteUser] = useDeleteUserMutation();
    const [deleteClient] = useDeleteClientMutation();
    const menuType = useSelector((state) => state.menuType);
    const { showNotification } = useNotification();

    useEffect(() => {
        const updateColumnWidths = () => {
            if (stickyColumnsCount > 0) {
                const newWidths = columnRefs.current
                    .slice(0, stickyColumnsCount)
                    .map((ref) => ref?.getBoundingClientRect().width || 0);

                if (
                    JSON.stringify(newWidths) !==
                        JSON.stringify(columnWidths) &&
                    newWidths.every((width) => width >= 0)
                ) {
                    setColumnWidths(newWidths);
                }
            }
        };

        window.addEventListener("resize", updateColumnWidths);
        updateColumnWidths();
        return () => window.removeEventListener("resize", updateColumnWidths);
    }, [stickyColumnsCount, data, columnWidths]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
        onSort && onSort(property, isAsc ? "desc" : "asc");
    };

    const handleSearch = (field, value) => {
        const newSearchFields = { ...searchFields, [field]: value };
        setSearchFields(newSearchFields);
        onSearch && onSearch(newSearchFields);
    };

    const handleEditClick = (rowData) => {
        setSelectedRowData(rowData);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRowData(null);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelected = displayedRows.map((row) => row.id);
            setSelectedRows(newSelected);
            onSelectRows?.(newSelected);
        } else {
            setSelectedRows([]);
            onSelectRows?.([]);
        }
    };

    const handleSelectRow = (event, id) => {
        const selectedIndex = selectedRows.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = [...selectedRows, id];
        } else {
            newSelected = selectedRows.filter((rowId) => rowId !== id);
        }

        setSelectedRows(newSelected);
        onSelectRows?.(newSelected);
    };

    const headersWithoutId =
        headers?.filter((header) => header.key !== "id") || [];
    const initialFlatHeaders = flattenHeaders(headersWithoutId, "", headerGap);
    const initialLeafHeaders = initialFlatHeaders.filter(
        (h) => !h.subheaders || h.subheaders.length === 0
    );

    const filterHeadersByVisibleColumns = (headers, visibleCols) => {
        if (!headers?.length) return [];
        if (!visibleCols.length) {
            return headers;
        }

        return headers.filter((header) => {
            const isKeyVisible = visibleCols.includes(header.key);
            const isStickyColumn = initialLeafHeaders
                .slice(0, stickyColumnsCount)
                .some((h) => h.key === header.key);
            return isStickyColumn || isKeyVisible;
        });
    };

    const filteredHeaders = filterHeadersByVisibleColumns(
        headersWithoutId,
        visibleColumns
    );

    const maxDepth = getHeaderDepth(filteredHeaders);
    const headerRows = organizeHeadersByRow(
        filteredHeaders,
        maxDepth,
        headerGap
    );
    const flatHeaders = flattenHeaders(filteredHeaders, "", headerGap);
    const leafHeaders = flatHeaders.filter(
        (h) => !h.subheaders || h.subheaders.length === 0
    );

    const sortedData = useMemo(() => {
        if (!orderBy) return data;

        const totalRows = data.filter((row) => {
            const firstColumnValue = getNestedValue(row, leafHeaders[0]?.key);
            const secondColumnValue = getNestedValue(row, leafHeaders[1]?.key);
            return (
                firstColumnValue === "Total" || secondColumnValue === "Total"
            );
        });

        const regularRows = data.filter((row) => {
            const firstColumnValue = getNestedValue(row, leafHeaders[0]?.key);
            const secondColumnValue = getNestedValue(row, leafHeaders[1]?.key);
            return (
                firstColumnValue !== "Total" && secondColumnValue !== "Total"
            );
        });

        const sortedRegularRows = [...regularRows].sort((a, b) => {
            const aValue = getNestedValue(a, orderBy);
            const bValue = getNestedValue(b, orderBy);

            // Parse values for sorting, handling percentages correctly
            const parsedAValue = parseValueForSorting(aValue);
            const parsedBValue = parseValueForSorting(bValue);

            // Check if both values are strings or both are numbers
            const aIsString = typeof parsedAValue === 'string';
            const bIsString = typeof parsedBValue === 'string';

            // If data types are different (one string, one number),
            // put numbers first in ascending, strings first in descending
            if (aIsString !== bIsString) {
                return (aIsString ? 1 : -1) * (order === 'asc' ? 1 : -1);
            }

            // Normal comparison for same data types
            if (parsedAValue < parsedBValue) return order === "asc" ? -1 : 1;
            if (parsedAValue > parsedBValue) return order === "asc" ? 1 : -1;
            return 0;
        });

        return [...sortedRegularRows, ...totalRows];
    }, [data, order, orderBy, leafHeaders]);

    const displayedRows = sortedData.slice(0, maxRows);

    const getLeftOffset = (columnIndex) => {
        if (!columnWidths.length || columnIndex < 0) return 0;

        return columnWidths
            .slice(0, columnIndex)
            .reduce((sum, width) => sum + (width || 0), 0);
    };

    const renderHeaderCell = (header, rowSpan = 1, colSpan = 1, rowIndex) => {
        if (header.isDummy) {
            return (
                <StyledTableCell
                    key={header.key}
                    rowSpan={rowSpan}
                    colSpan={colSpan}
                    sx={{
                        width: "10px",
                        padding: 0,
                        border: "none",
                        backgroundColor: "#faf9f8",
                        "&::before": {
                            content: '""',
                            display: "block",
                        },
                    }}
                />
            );
        }

        const columnIndex = leafHeaders.findIndex((h) => h.key === header.key);
        return (
            <StyledTableCell
                key={header.key}
                align={alignItems || "center"}
                rowSpan={rowSpan}
                colSpan={colSpan}
                variant="head"
                padding="normal"
                columnIndex={columnIndex}
                stickyColumnsCount={isColumnSticky ? stickyColumnsCount : 0}
                setHeaderHeight={setHeaderHeight}
                rowIndex={rowIndex}
                maxDepth={maxDepth}
                className={`common-table__header common-table__header--row-${rowIndex}`}
                ref={(el) => {
                    if (columnIndex < stickyColumnsCount) {
                        columnRefs.current[columnIndex] = el;
                    }
                }}
                sx={{
                    fontSize: headerFontSizes?.[rowIndex] || "14px",
                    verticalAlign: header.align_item
                        ? header.align_item
                        : "middle",
                    height: headerHeights?.[rowIndex] || "1.5rem",
                    padding: headerPadding?.[rowIndex] || "5px 8px",
                    fontWeight:
                        isHeaderBold &&
                        rowIndex === 0 &&
                        !header.key.startsWith(leafHeaders[0].key)
                            ? 700
                            : 400,
                    color:
                        isHeaderBold &&
                        rowIndex === 0 &&
                        header.key.startsWith(leafHeaders[0].key)
                            ? "10px"
                            : "0px",
                    backgroundColor:
                        header.key.includes("FY-") ||
                        (header.key.toLowerCase().includes("ytd") &&
                            !["ytd_revenue", "YTD_effort_total"].includes(
                                header.key
                            ))
                            ? "#F6F3EC"
                            : "#faf9f8",
                    boxShadow:
                        (isColumnSticky &&
                            columnIndex === stickyColumnsCount - 1) ||
                        header.key === "top_25revenue"
                            ? "3px -1px 4px 0px #A8A8A840"
                            : "none",
                    position:
                        (isColumnSticky && columnIndex < stickyColumnsCount) ||
                        header.key === "top_25revenue"
                            ? "sticky"
                            : "static",
                    left:
                        isColumnSticky &&
                        columnIndex < stickyColumnsCount &&
                        header.key !== "top_25revenue"
                            ? `${getLeftOffset(columnIndex)}px`
                            : header.key === "top_25revenue"
                            ? "0"
                            : "auto",
                    zIndex:
                        (isColumnSticky && columnIndex < stickyColumnsCount) ||
                        header.key === "top_25revenue"
                            ? header.key === "top_25revenue"
                                ? "6 !important"
                                : 4
                            : 3,
                    minWidth: header.key.startsWith(leafHeaders[0].key)
                        ? firstColumnWidth
                        : "1.5rem",
                    ...(highlightKeys.includes(header.key)
                        ? {
                              color: "#ED1C24",
                              fontWeight: "bold",
                          }
                        : {}),
                    ...(header.isGroup && {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                    }),
                    ...(header.key.includes("rank")
                        ? {
                              textAlign: "center",
                          }
                        : {}),
                    border: "none",
                    ...(header.borderLeft
                        ? {
                              borderLeft: "1px solid #D9D9D9 !important",
                              paddingLeft: "15px",
                              paddingRight: "10px",
                          }
                        : {}),
                    ...(header.paddingLeft
                        ? {
                              paddingLeft: "15px",
                          }
                        : {}),
                    ...(header.paddingRight
                        ? {
                              paddingRight: header.paddingRight,
                          }
                        : {}),
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: header.key === "client_name" ? "150px" : "auto",
                        alignItems:
                            (alignItems && "flex-start") ||
                            (header.key.startsWith(leafHeaders[0].key) &&
                                !leafHeaders[0].key.includes("rank")) ||
                            header.key === "client_name" || header.key === 'username'
                                ? "flex-start"
                                : "center",
                        ...(header.key.includes("client") &&
                        header.key !== "client_query" && header.key !== "client_volume" && header.key !== 'client_meeting' && header.key !== 'corporate_client_meeting'
                            ? {
                                  alignItems: "flex-start",
                              }
                            : {}),
                        ...(header.textAlign
                            ? {
                                  alignItems: header.textAlign,
                              }
                            : {}),
                        ...(header.textAlign
                            ? {
                                  alignItems: header.textAlign,
                              }
                            : {}),
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems:
                                (alignItems && "flex-start") || "center",
                            cursor: header.sortable ? "pointer" : "default",
                        }}
                        onClick={() => {
                            if (header.sortable) {
                                handleRequestSort(header.key);
                                setActiveHeader(header.key);
                            }
                        }}
                    >
                        <div
                            style={{
                                textAlign:
                                    header.key.includes("client") &&
                                    header.key !== "client_query" && header.key !== "client_volume" && header.key !== 'client_meeting' && header.key !== 'corporate_client_meeting'
                                        ? "left"
                                        : "center",
                                width: "100%",
                            }}
                        >
                            {header.name}
                            {header.sortable &&
                                (!header.subheaders ||
                                    header.subheaders.length === 0) && (
                                    <StyledTableSortLabel
                                        active={orderBy === header.key}
                                        direction={
                                            orderBy === header.key
                                                ? order
                                                : "asc"
                                        }
                                    />
                                )}
                        </div>
                    </Box>
                    {header.subTitle && <div>{header.subTitle}</div>}
                    {header.key === "delta" && (
                        <ChangeHistoryIcon
                            sx={{
                                color: "#ED1C24",
                                marginTop: "5px",
                            }}
                        />
                    )}
                    {header.searchable &&
                        (!header.subheaders ||
                            header.subheaders.length === 0) && (
                            <TextField
                                size="small"
                                placeholder="Search..."
                                variant="outlined"
                                onChange={(e) =>
                                    handleSearch(header.key, e.target.value)
                                }
                                value={searchFields[header.key] || ""}
                                sx={{ mt: 1 }}
                                InputProps={{
                                    endAdornment: <Search fontSize="small" />,
                                }}
                            />
                        )}
                </Box>
            </StyledTableCell>
        );
    };

    const hasTotalRow = useMemo(() => {
        return displayedRows.some((row) => {
            const firstColumnValue =
                getNestedValue(row, leafHeaders[0]?.key) ||
                getNestedValue(row, leafHeaders[1]?.key);
            return firstColumnValue === "Total";
        });
    }, [displayedRows, leafHeaders]);

    const handleDelete = async () => {
        try {
            if (dialogType === "client") {
                await Promise.all(
                    selectedRows.map((id) => deleteClient(id))
                ).then(() => {
                    showNotification("success", "Client disabled successfully");
                });
            } else {
                await Promise.all(
                    selectedRows.map((id) => deleteUser(id))
                ).then(() => {
                    showNotification("success", "User disabled successfully");
                });
            }
            refetch();
            setSelectedRows([]);
            onSelectRows?.([]);
        } catch (error) {
            console.error("Error deleting users:", error);
        }
    };

    return (
        <>
            {showCheckboxColumn && (
                <div className="flex justify-between items-center px-4 py-2 xl:py-1.5 2xl:py-2 3xl:py-2.5 bg-[#9E906E0D]">
                    <p
                        className={classNames(
                            "text-base xl:text-xs 2xl:text-base 3xl:text-md",
                            {
                                "text-[#9E906E]": selectedRows.length === 0,
                                "text-[#ED1C24]": selectedRows.length > 0,
                            }
                        )}
                    >
                        {selectedRows.length} User(s) selected
                    </p>
                    <button
                        onClick={handleDelete}
                        disabled={selectedRows.length === 0}
                        className={classNames(
                            "text-base xl:text-xs 2xl:text-base 3xl:text-md flex items-center gap-2 px-2 py-1 rounded",
                            "text-[#ED1C24] disabled:opacity-50 hover:bg-[rgba(237,28,36,0.04)]"
                        )}
                    >
                        <DeleteIcon style={{ fontSize: "1rem" }} />
                        INACTIVE
                    </button>
                </div>
            )}
            <StyledTableContainer
                component={Paper}
                elevation={0}
                square
                style={
                    hasPagination ? { height: "100%", overflowY: "auto" } : {}
                }
            >
                <Table
                    aria-label={`${title
                        ?.toLowerCase()
                        .replace(/\s+/g, "-")}-table`}
                    size="small"
                    stickyHeader={hasPagination}
                >
                    <TableHead
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 3,
                            fontWeight: "bold",
                        }}
                    >
                        {headerRows.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {showCheckboxColumn && rowIndex === 0 && (
                                    <StyledTableCell
                                        padding="checkbox"
                                        rowSpan={headerRows.length}
                                        sx={{
                                            // left: "0.5rem",
                                            zIndex: 4,
                                            backgroundColor: "#faf9f8",
                                        }}
                                    >
                                        <StyledCheckbox
                                            color="primary"
                                            indeterminate={
                                                selectedRows.length > 0 &&
                                                selectedRows.length <
                                                    displayedRows.length
                                            }
                                            checked={
                                                displayedRows.length > 0 &&
                                                selectedRows.length ===
                                                    displayedRows.length
                                            }
                                            onChange={handleSelectAll}
                                        />
                                    </StyledTableCell>
                                )}
                                {row.map((header) => {
                                    return renderHeaderCell(
                                        header,
                                        header.rowSpan,
                                        header.colSpan,
                                        rowIndex
                                    );
                                })}
                                {showEditColumn && rowIndex === 0 && (
                                    <StyledTableCell
                                        align="center"
                                        variant="head"
                                        padding="normal"
                                        sx={{
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 4,
                                            fontSize: "16px",
                                            backgroundColor: "#faf9f8",
                                        }}
                                    >
                                        Edit
                                    </StyledTableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {displayedRows.map((row, rowIndex) => {
                            const isTotalFirstColumn =
                                getNestedValue(row, leafHeaders[0]?.key) ===
                                "Total";
                            const isTotalSecondColumn =
                                getNestedValue(row, leafHeaders[1]?.key) ===
                                "Total";
                            const isTotal =
                                isTotalFirstColumn || isTotalSecondColumn;
                            const RowComponent =
                                isTotal && hasTotalRow
                                    ? StyledTotalRow
                                    : StyledTableRow;

                            let rowStyles = {};

                            if (highlightRules.length > 0) {
                                for (const rule of highlightRules) {
                                    let {
                                        column,
                                        condition,
                                        value,
                                        backgroundColor,
                                        borderColor,
                                        fetchCustomRowColor,
                                    } = rule;

                                    const cellValue = Number(
                                        getNestedValue(row, column)
                                    );
                                    let matches = false;

                                    switch (condition) {
                                        case "equals":
                                            matches = cellValue === value;
                                            break;
                                        case "lessThan":
                                            matches = cellValue < value;
                                            break;
                                        case "lessThanOrEqual":
                                            matches = cellValue <= value;
                                            break;
                                        case "greaterThan":
                                            matches = cellValue > value;
                                            break;
                                        case "greaterThanOrEqual":
                                            matches = cellValue >= value;
                                            break;
                                        case "between":
                                            matches =
                                                cellValue >= value[0] &&
                                                cellValue <= value[1];
                                            break;
                                        case "customRowColor":
                                            const colors =
                                                fetchCustomRowColor(row);
                                            borderColor = colors?.borderColor;
                                            backgroundColor =
                                                colors?.backgroundColor;
                                            matches = true;
                                            break;
                                        default:
                                            matches = false;
                                    }

                                    if (matches) {
                                        rowStyles = {
                                            backgroundColor: backgroundColor,
                                            borderLeft: `2px solid ${borderColor}`,
                                        };
                                        break;
                                    }
                                }
                            }

                            return (
                                <RowComponent
                                    key={rowIndex}
                                    sx={{
                                        ...rowStyles,
                                        "& .MuiTableCell-root:not([data-dummy='true'])":
                                            {
                                                borderBottom:
                                                    "1px solid rgba(224, 224, 224, 1)",
                                            },
                                        "&:hover": {
                                            backgroundColor:
                                                rowStyles.backgroundColor ||
                                                "inherit",
                                        },
                                        ...(isTotal && {
                                            backgroundColor: "#faf9f8",
                                            "&:hover": {
                                                backgroundColor: "#faf9f8",
                                            },
                                        }),
                                    }}
                                >
                                    {showCheckboxColumn && (
                                        <TableCell padding="checkbox">
                                            <StyledCheckbox
                                                color="primary"
                                                checked={
                                                    selectedRows.indexOf(
                                                        row.id
                                                    ) !== -1
                                                }
                                                onChange={(event) =>
                                                    handleSelectRow(
                                                        event,
                                                        row.id
                                                    )
                                                }
                                            />
                                        </TableCell>
                                    )}
                                    {leafHeaders.map((header, columnIndex) => (
                                        <TableCell
                                            key={header.key}
                                            align={
                                                alignItems || columnIndex === 0
                                                    ? "left"
                                                    : "center"
                                            }
                                            variant="body"
                                            padding="normal"
                                            className="common-table__cell"
                                            data-dummy={
                                                header.isDummy
                                                    ? "true"
                                                    : undefined
                                            }
                                            sx={{
                                                ...(header.isDummy
                                                    ? {
                                                          padding: 0,
                                                          border: "none",
                                                          backgroundColor:
                                                              "white",
                                                          "&::before": {
                                                              content: '""',
                                                              display: "block",
                                                              width: "30px",
                                                          },
                                                      }
                                                    : {
                                                          borderBottom:
                                                              "1px solid rgba(224, 224, 224, 1)",
                                                          borderRight:
                                                              header.key.includes(
                                                                  "Q"
                                                              ) &&
                                                              header.key.endsWith(
                                                                  "Actual"
                                                              )
                                                                  ? "2px solid rgba(224, 224, 224, 1)"
                                                                  : "none",
                                                          borderLeft: "none",
                                                      }),
                                                ...(header.borderLeft
                                                    ? {
                                                          borderLeft:
                                                              "1px solid #D9D9D9 !important",
                                                          paddingLeft: "15px",
                                                          paddingRight: "10px",
                                                      }
                                                    : {}),
                                                ...(columnIndex <
                                                    stickyColumnsCount &&
                                                    isColumnSticky && {
                                                        position: "sticky",
                                                        left: `${getLeftOffset(
                                                            columnIndex
                                                        )}px`,
                                                        zIndex: 2,
                                                        boxShadow:
                                                            "3px 0px 4px 0px #A8A8A840",
                                                        backgroundColor: `white`,
                                                        backgroundImage: `linear-gradient(to bottom, ${
                                                            getNestedValue(
                                                                row,
                                                                "tier_color"
                                                            ) || "white"
                                                        }, ${
                                                            getNestedValue(
                                                                row,
                                                                "tier_color"
                                                            ) || "white"
                                                        })`,
                                                        minWidth:
                                                            columnIndex === 0
                                                                ? firstColumnWidth
                                                                : "auto",
                                                    }),
                                                ...(columnIndex !== 0 && {
                                                    paddingLeft: "0px",
                                                }),
                                                ...(alignItems && {
                                                    paddingLeft: "10px",
                                                }),
                                                paddingRight: "30px",
                                                ...(isTotal && {
                                                    fontWeight: "bold",
                                                    color: "#ED1C24",
                                                }),
                                                ...(highlightKeys.includes(
                                                    header.key
                                                ) && {
                                                    color: "#ED1C24",
                                                    fontWeight: "bold",
                                                }),
                                                ...(header.key.includes("rank")
                                                    ? {
                                                          textAlign: "center",
                                                      }
                                                    : {}),

                                                ...(header.key.includes(
                                                    "FY-"
                                                ) ||
                                                header.key
                                                    .toLowerCase()
                                                    .includes("ytd")
                                                    ? {
                                                          backgroundColor:
                                                              "#faf9f8",
                                                      }
                                                    : {}),
                                                ...(!header.sortable
                                                    ? { paddingLeft: "20px" }
                                                    : {}),
                                                ...(header.key.includes(
                                                    "client_name"
                                                )
                                                    ? {
                                                          textAlign: "left",
                                                          paddingLeft: "0px",
                                                      }
                                                    : {}),
                                                ...(customCellColor &&
                                                    customCellColor(
                                                        header,
                                                        row
                                                    )),
                                                ...(header.key.includes(
                                                    "client"
                                                ) &&
                                                header.key !== "client_query" && header.key !== "client_volume" && header.key !== 'client_meeting' && header.key !== 'corporate_client_meeting'
                                                    ? {
                                                          textAlign: "left",
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            {header.isDummy ? (
                                                ""
                                            ) : columnIndex === 0 &&
                                              isTotalFirstColumn ? (
                                                totalText
                                            ) : columnIndex === 1 &&
                                              isTotalSecondColumn ? (
                                                totalText
                                            ) : (header.key === "username" ||
                                                  header.key ===
                                                      "client_name") &&
                                              !isTotal ? (
                                                <>
                                                    {menuType !== "admin" &&
                                                    (row.user_role
                                                        ? [
                                                              "head_of_research",
                                                              "hybrid",
                                                              "analyst",
                                                          ].includes(
                                                              row.user_role
                                                          )
                                                        : true) ? (
                                                        <>
                                                            {isClient ? (
                                                                <Link
                                                                    to={`/client-performance/${getNestedValue(
                                                                        row,
                                                                        "id"
                                                                    )}`}
                                                                >
                                                                    {getNestedValue(
                                                                        row,
                                                                        header.key
                                                                    )}
                                                                </Link>
                                                            ) : (
                                                                <Link
                                                                    to={`/analyst-engagement/${getNestedValue(
                                                                        row,
                                                                        "id"
                                                                    )}`}
                                                                >
                                                                    <AnalystName
                                                                        name={getNestedValue(
                                                                            row,
                                                                            header.key
                                                                        )}
                                                                        values={
                                                                            row
                                                                        }
                                                                    />
                                                                </Link>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="analyst-name-cell">
                                                            <div className="analyst-name-container">
                                                                <AnalystName
                                                                    name={getNestedValue(
                                                                        row,
                                                                        header.key
                                                                    )}
                                                                    values={row}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            ) : typeof getNestedValue(
                                                  row,
                                                  header.key
                                              ) === "boolean" ? (
                                                getNestedValue(
                                                    row,
                                                    header.key
                                                ) ? (
                                                    "Yes"
                                                ) : (
                                                    "No"
                                                )
                                            ) : customCellRender &&
                                              customCellRender[header.key] ? (
                                                customCellRender[header.key](
                                                    row,
                                                    header.key
                                                )
                                            ) : getNestedValue(
                                                  row,
                                                  header.key
                                              ) !== "null" ? (
                                                getNestedValue(row, header.key)
                                            ) : (
                                                ""
                                            )}
                                        </TableCell>
                                    ))}
                                    {showEditColumn && (
                                        <TableCell align="center">
                                            <IconButton
                                                onClick={() =>
                                                    handleEditClick(row)
                                                }
                                                sx={{ padding: 0 }}
                                            >
                                                <EditIcon
                                                    sx={{ fontSize: "1rem" }}
                                                />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </RowComponent>
                            );
                        })}
                    </TableBody>
                </Table>
            </StyledTableContainer>
            {showEditColumn && (
                <CommonDialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    rowData={selectedRowData}
                    dialogType={dialogType}
                />
            )}
        </>
    );
};

CommonTable.propTypes = {
    title: PropTypes.string,
    headers: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    maxRows: PropTypes.number,
    dateRange: PropTypes.object,
    onDateRangeChange: PropTypes.func,
    hasPagination: PropTypes.bool,
    totalText: PropTypes.string,
    firstColumnWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isHeaderBold: PropTypes.bool,
    isColumnSticky: PropTypes.bool,
    stickyColumnsCount: PropTypes.number,
    headerFontSizes: PropTypes.arrayOf(PropTypes.string),
    highlightRules: PropTypes.arrayOf(
        PropTypes.shape({
            column: PropTypes.string.isRequired,
            condition: PropTypes.oneOf([
                "equals",
                "lessThan",
                "lessThanOrEqual",
                "greaterThan",
                "greaterThanOrEqual",
                "between",
            ]).isRequired,
            value: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.arrayOf(PropTypes.number),
            ]).isRequired,
            backgroundColor: PropTypes.string.isRequired,
            borderColor: PropTypes.string.isRequired,
        })
    ),
    showCheckboxColumn: PropTypes.bool,
    onSelectRows: PropTypes.func,
    headerHeights: PropTypes.arrayOf(PropTypes.string),
    headerGap: PropTypes.bool,
};

CommonTable.defaultProps = {
    hasPagination: false,
    totalText: "Total",
    firstColumnWidth: "auto",
    isHeaderBold: false,
    isColumnSticky: false,
    stickyColumnsCount: 0,
    headerFontSizes: ["16px", "16px", "16px"],
    showEditColumn: false,
    showCheckboxColumn: false,
    headerHeights: ["1.5rem", "1.5rem", "1.5rem"],
    headerGap: false,
};

export default CommonTable;
